// FILE: src/components/Give.jsx
import React from "react";

export default function Give() {
  // 🔁 Replace with your real details
  const bank = {
    accountName: "The Equip Foundation",
    bankName: "Standard Bank",
    accountNumber: "10269232956",
    branchCode: "011842",
    accountType: "Current",
    reference: "Your Name + Purpose (Camp / Media / Other)",
  };

  const contact = {
    email: "info@equipfoundation.com",
    whatsapp: "+27 67 096 4390",
  };

  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      alert("Copied ✅");
    } catch {
      alert("Could not copy. Please copy manually.");
    }
  };

  return (
    <div className="min-h-[calc(100vh-120px)] bg-gradient-to-br from-slate-50 to-white">
      <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        {/* Main Card */}
        <div className="bg-white border rounded-2xl p-6 shadow-sm">
          <h2 className="text-2xl font-bold">Give to the Ministry</h2>

          <p className="mt-2 text-sm text-slate-600">
            We are a non-profit ministry and we depend on donations to serve the
            community. Your generosity helps us disciple young people, run camps,
            support outreach initiatives, and create Christ-centered content that
            equips believers.
          </p>

          {/* Quick action bar */}
          <div className="mt-4 flex flex-col sm:flex-row gap-2">
            <button
              onClick={() => copyText(bank.accountNumber)}
              className="w-full sm:w-auto px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 transition"
            >
              Copy Account Number
            </button>
          </div>

          <div className="mt-5 grid gap-4 md:grid-cols-2">
            {/* Banking Details */}
            <div className="border rounded-2xl p-4 bg-slate-50">
              <h3 className="font-semibold text-slate-800">Banking Details</h3>

              <div className="mt-3 space-y-2 text-sm">
                <div className="flex items-start justify-between gap-4">
                  <span className="text-slate-500">Account Name</span>
                  <span className="font-medium text-right">{bank.accountName}</span>
                </div>

                <div className="flex items-start justify-between gap-4">
                  <span className="text-slate-500">Bank</span>
                  <span className="font-medium text-right">{bank.bankName}</span>
                </div>

                <div className="flex items-start justify-between gap-4">
                  <span className="text-slate-500">Account Number</span>
                  <div className="text-right">
                    <div className="font-medium">{bank.accountNumber}</div>
                    <button
                      onClick={() => copyText(bank.accountNumber)}
                      className="mt-1 text-xs text-indigo-600 hover:underline"
                    >
                      Copy
                    </button>
                  </div>
                </div>

                <div className="flex items-start justify-between gap-4">
                  <span className="text-slate-500">Branch Code</span>
                  <span className="font-medium text-right">{bank.branchCode}</span>
                </div>

                <div className="flex items-start justify-between gap-4">
                  <span className="text-slate-500">Account Type</span>
                  <span className="font-medium text-right">{bank.accountType}</span>
                </div>

                <div className="pt-3 border-t">
                  <div className="text-slate-500 text-xs">Suggested Reference</div>
                  <div className="mt-1 font-medium">{bank.reference}</div>
                </div>
              </div>
            </div>

            {/* Where Giving Goes */}
            <div className="border rounded-2xl p-4 bg-white">
              <h3 className="font-semibold text-slate-800">Where Your Giving Goes</h3>

              <ul className="mt-3 space-y-2 text-sm text-slate-700 list-disc list-inside">
                <li>Youth discipleship programs & teaching materials</li>
                <li>Fully sponsored youth camp experiences</li>
                <li>School Shoe Drive for learners in need</li>
                <li>Community outreach & practical support</li>
                <li>
                  Improving ministry recording equipment (cameras, sound, lighting)
                  to produce quality Christian content for our platforms
                </li>
              </ul>

              <div className="mt-4 p-3 rounded-2xl bg-emerald-50 border border-emerald-100">
                <div className="text-xs uppercase tracking-wide text-emerald-700">
                  Current Focus Areas
                </div>
                <div className="mt-1 text-sm text-emerald-900">
                  If you would like to support a specific initiative, please use:
                  <div className="mt-2 space-y-1">
                    <div>
                      <strong>Camp</strong> – Youth Camp
                    </div>
                    <div>
                      <strong>Shoes</strong> – School Shoe Drive
                    </div>
                    <div>
                      <strong>Media</strong> – Equipment &amp; Content Development
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-4 text-sm text-slate-600">
                <div className="font-medium text-slate-800">Need assistance?</div>
                <div className="mt-1">
                  Email:{" "}
                  <a
                    href={`mailto:${contact.email}`}
                    className="font-medium text-indigo-600 hover:underline"
                  >
                    {contact.email}
                  </a>
                </div>
                <div className="mt-1">
                  WhatsApp:{" "}
                  <a
                    href={`https://wa.me/${contact.whatsapp.replace(/\D/g, "")}`}
                    target="_blank"
                    rel="noreferrer"
                    className="font-medium text-indigo-600 hover:underline"
                  >
                    {contact.whatsapp}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Campaign Highlight Cards */}
        <div className="grid gap-4 md:grid-cols-3">
          <div className="bg-white border rounded-2xl p-5 shadow-sm">
            <h3 className="text-lg font-semibold">Youth Camp</h3>
            <p className="mt-2 text-sm text-slate-600">
              All camp expenses are covered, including accommodation,
              food, and discipleship materials.
            </p>
            <div className="mt-3 text-sm">
              Reference: <span className="font-semibold">Camp</span>
            </div>
          </div>

          <div className="bg-white border rounded-2xl p-5 shadow-sm">
            <h3 className="text-lg font-semibold">School Shoe Drive</h3>
            <p className="mt-2 text-sm text-slate-600">
              We provide school shoes to learners in need, restoring dignity and
              confidence.
            </p>
            <div className="mt-3 text-sm">
              Reference: <span className="font-semibold">Shoes</span>
            </div>
          </div>

          <div className="bg-white border rounded-2xl p-5 shadow-sm">
            <h3 className="text-lg font-semibold">Media &amp; Equipment</h3>
            <p className="mt-2 text-sm text-slate-600">
              We are improving our cameras, microphones, and lighting to produce
              quality teaching content for our Bible Trip Podcast and in future
              other Christian platforms.
            </p>
            <div className="mt-3 text-sm">
              Reference: <span className="font-semibold">Media</span>
            </div>
          </div>
        </div>

        {/* Minimal spacer */}
        <div className="h-2" />
      </div>
    </div>
  );
}